import { useEffect, useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import { Select } from '../../../shared/components/Select';
import { Toggle } from '../../../shared/components/Toggle';
import Button from '../../../shared/components/Button';
import { useCategories } from '../../products/hooks/useMetaData';
import { useMyReturnPolicy, useUpdateMyReturnPolicy } from '../hooks/useReturnsPolicy';
import type { ReturnInventoryRestockTrigger } from '../api/returnsPolicyApi';
import { buildReturnPolicyPayload } from '../payload';

const RESTOCK_OPTIONS: { value: ReturnInventoryRestockTrigger; title: string }[] = [
  { value: 'ON_RECEIPT', title: 'When the returned item is received' },
  { value: 'ON_APPROVAL', title: 'As soon as the request is approved' },
];

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <h2 className="text-[16px] font-semibold text-foreground">{title}</h2>
      {description && <p className="text-[13px] text-muted-foreground mt-0.5 mb-4">{description}</p>}
      <div className={description ? '' : 'mt-4'}>{children}</div>
    </div>
  );
}

/**
 * Merchant returns & exchanges policy (PE-2163).
 *
 * Writes go to watchman's merchant self-service `PATCH /merchants/me/store-config`,
 * which resolves the merchant from the JWT — so this page is MERCHANT-scoped only.
 * TCS staff see a merchant's policy read-only on the merchant detail page instead.
 *
 * This is the policy half of PE-2163. The returns/exchanges QUEUE is still blocked:
 * order-service owns no returns domain yet (PE-2162) and osprey-api does not proxy
 * /returns (PE-2231).
 */
export function ReturnsPolicyPage() {
  const roleScope = useAuthStore((s) => s.user?.roleScope);
  const isMerchantScoped = roleScope === 'MERCHANT';

  const policyQuery = useMyReturnPolicy(isMerchantScoped);
  const updatePolicy = useUpdateMyReturnPolicy();
  const categoriesQuery = useCategories();
  const categories = categoriesQuery.data?.data ?? [];

  const [returnsEnabled, setReturnsEnabled] = useState(false);
  const [returnWindowDays, setReturnWindowDays] = useState('');
  const [returnShipmentWindowDays, setReturnShipmentWindowDays] = useState('');
  const [ineligibleCategoryIds, setIneligibleCategoryIds] = useState<string[]>([]);
  const [restockingFeeEnabled, setRestockingFeeEnabled] = useState(false);
  const [restockingFeePercentage, setRestockingFeePercentage] = useState('');
  const [restockInventoryTrigger, setRestockInventoryTrigger] = useState<ReturnInventoryRestockTrigger>('ON_RECEIPT');
  const [absorbShipping, setAbsorbShipping] = useState(false);
  const [policyText, setPolicyText] = useState('');

  const config = policyQuery.data;

  useEffect(() => {
    if (!config) return;
    setReturnsEnabled(!!config.returnsEnabled);
    setReturnWindowDays(config.returnWindowDays != null ? String(config.returnWindowDays) : '');
    setReturnShipmentWindowDays(
      config.returnShipmentWindowDays != null ? String(config.returnShipmentWindowDays) : '',
    );
    setIneligibleCategoryIds(config.ineligibleCategoryIds ?? []);
    setRestockingFeeEnabled(!!config.restockingFeeEnabled);
    setRestockingFeePercentage(
      config.restockingFeePercentage != null ? String(config.restockingFeePercentage) : '',
    );
    setRestockInventoryTrigger(config.restockInventoryTrigger ?? 'ON_RECEIPT');
    setAbsorbShipping(!!config.returnShippingCostAbsorbedByMerchant);
    setPolicyText(config.returnPolicy ?? '');
  }, [config]);

  const toggleCategory = (id: string) =>
    setIneligibleCategoryIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  const save = () =>
    updatePolicy.mutate(
      buildReturnPolicyPayload({
        returnsEnabled,
        returnWindowDays,
        returnShipmentWindowDays,
        ineligibleCategoryIds,
        restockingFeeEnabled,
        restockingFeePercentage,
        restockInventoryTrigger,
        absorbShipping,
        policyText,
      }),
    );

  const Heading = (
    <div className="mb-5">
      <h1 className="text-[22px] font-semibold text-foreground">Returns &amp; Exchanges</h1>
      <p className="text-[13px] text-muted-foreground mt-1">
        The rules customers see when they ask to return or exchange an order. Editable at any time —
        changes apply to new requests.
      </p>
    </div>
  );

  if (!isMerchantScoped) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        {Heading}
        <div className="bg-card rounded-2xl border border-border p-6 text-[14px] text-muted-foreground">
          A returns policy belongs to a single merchant and is edited by that merchant. Open a
          merchant from <span className="text-foreground font-medium">Merchants</span> to view theirs.
        </div>
      </div>
    );
  }

  if (policyQuery.isLoading) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        {Heading}
        <div className="bg-card rounded-2xl border border-border p-6 text-[14px] text-muted-foreground">
          Loading returns policy…
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 max-w-4xl mx-auto px-2 space-y-5">
      {Heading}

      <Section
        title="Accept returns & exchanges"
        description="While this is off, customers cannot open a return or exchange request at all."
      >
        <Toggle label="Returns and exchanges enabled" checked={returnsEnabled} onChange={setReturnsEnabled} />
      </Section>

      <Section
        title="Time windows"
        description="Leave a field blank for no limit."
      >
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Request window (days after delivery)"
            type="number"
            min={0}
            value={returnWindowDays}
            onChange={(e) => setReturnWindowDays(e.target.value)}
            disabled={!returnsEnabled}
          />
          <Input
            label="Return-shipment window (days after approval)"
            type="number"
            min={0}
            value={returnShipmentWindowDays}
            onChange={(e) => setReturnShipmentWindowDays(e.target.value)}
            disabled={!returnsEnabled}
          />
        </div>
        <p className="text-[12px] text-muted-foreground mt-2">
          An exchange auto-cancels if the customer has not shipped the item back within the
          return-shipment window.
        </p>
      </Section>

      <Section
        title="Ineligible categories"
        description="Products in these categories cannot be returned or exchanged."
      >
        {categoriesQuery.isLoading ? (
          <p className="text-[13px] text-muted-foreground">Loading categories…</p>
        ) : categories.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No product categories yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => {
              const selected = ineligibleCategoryIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCategory(c.id)}
                  disabled={!returnsEnabled}
                  className={`px-3 py-1.5 rounded-full border text-[13px] transition-colors disabled:opacity-50 ${
                    selected
                      ? 'bg-foreground text-background border-foreground'
                      : 'bg-card text-foreground border-border hover:border-foreground'
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
      </Section>

      <Section title="Restocking fee" description="Deducted from the refund when a return is resolved as a refund.">
        <div className="space-y-4">
          <Toggle
            label="Charge a restocking fee"
            checked={restockingFeeEnabled}
            onChange={setRestockingFeeEnabled}
          />
          {restockingFeeEnabled && (
            <div className="max-w-[240px]">
              <Input
                label="Fee (% of the refunded line)"
                type="number"
                min={0}
                max={100}
                value={restockingFeePercentage}
                onChange={(e) => setRestockingFeePercentage(e.target.value)}
                disabled={!returnsEnabled}
              />
            </div>
          )}
        </div>
      </Section>

      <Section title="Inventory & shipping">
        <div className="space-y-4">
          <div className="max-w-[360px]">
            <Select
              label="Restock the original item"
              options={RESTOCK_OPTIONS}
              value={restockInventoryTrigger}
              onChange={(v) => setRestockInventoryTrigger(v as ReturnInventoryRestockTrigger)}
            />
          </div>
          <Toggle
            label="We cover the return-shipping cost"
            checked={absorbShipping}
            onChange={setAbsorbShipping}
          />
          <p className="text-[12px] text-muted-foreground">
            When off, the return-shipping cost is shown to the customer as theirs to pay.
          </p>
        </div>
      </Section>

      <Section title="Policy shown to customers" description="Free text displayed on the storefront.">
        <TextArea value={policyText} onChange={(e) => setPolicyText(e.target.value)} />
      </Section>

      <div className="flex justify-end">
        <Button size="sm" className="h-[40px]" onClick={save} disabled={updatePolicy.isPending} loading={updatePolicy.isPending}>
          Save policy
        </Button>
      </div>
    </div>
  );
}
